import type { ShapeTessellation, Vec2 } from '../types';

/**
 * Footprint tessellation.
 *
 * A panel's top surface is a triangulated 2D footprint whose vertices are later
 * lifted by the height field. Two tessellators cover every shape the roadmap
 * needs:
 *
 * - `tessellateGrid` produces the axis-aligned lattice a square wants, where a
 *   regular grid gives the most even triangle sizes.
 * - `tessellateConvexPolygon` subdivides any convex outline by fanning it from
 *   the centroid, which is how hexagons, triangles and octagons (Phase 2) are
 *   handled without writing a new tessellator for each.
 *
 * Both return their boundary loop in counter-clockwise order. The mesh builder
 * relies on that to stitch the side walls, so a new shape only has to get its
 * outline winding right to be watertight.
 */

/** Merges vertices that land on the same point, to 0.1 µm. */
class VertexWelder {
  private readonly lookup = new Map<string, number>();
  private readonly coords: number[] = [];

  add(x: number, y: number): number {
    const key = `${Math.round(x * 1e4)}:${Math.round(y * 1e4)}`;
    const existing = this.lookup.get(key);
    if (existing !== undefined) return existing;

    const index = this.coords.length / 2;
    this.coords.push(x, y);
    this.lookup.set(key, index);
    return index;
  }

  get positions(): Float32Array {
    return Float32Array.from(this.coords);
  }

  get count(): number {
    return this.coords.length / 2;
  }
}

/** A regular grid over a rectangle centred on the origin. */
export function tessellateGrid(width: number, height: number, segments: number): ShapeTessellation {
  const n = Math.max(1, Math.round(segments));
  const stride = n + 1;
  const positions = new Float32Array(stride * stride * 2);

  for (let j = 0; j <= n; j += 1) {
    for (let i = 0; i <= n; i += 1) {
      const index = (j * stride + i) * 2;
      positions[index] = -width / 2 + (i / n) * width;
      positions[index + 1] = -height / 2 + (j / n) * height;
    }
  }

  const triangles = new Uint32Array(n * n * 6);
  let t = 0;
  for (let j = 0; j < n; j += 1) {
    for (let i = 0; i < n; i += 1) {
      const a = j * stride + i;
      const b = a + 1;
      const c = a + stride;
      const d = c + 1;

      // Alternating the split direction stops the diagonals from lining up into
      // a visible corduroy across the printed surface.
      if ((i + j) % 2 === 0) {
        triangles[t++] = a;
        triangles[t++] = b;
        triangles[t++] = d;
        triangles[t++] = a;
        triangles[t++] = d;
        triangles[t++] = c;
      } else {
        triangles[t++] = a;
        triangles[t++] = b;
        triangles[t++] = c;
        triangles[t++] = b;
        triangles[t++] = d;
        triangles[t++] = c;
      }
    }
  }

  const boundary = new Uint32Array(n * 4);
  let b = 0;
  for (let i = 0; i < n; i += 1) boundary[b++] = i; // bottom, left to right
  for (let j = 0; j < n; j += 1) boundary[b++] = j * stride + n; // right, bottom to top
  for (let i = n; i > 0; i -= 1) boundary[b++] = n * stride + i; // top, right to left
  for (let j = n; j > 0; j -= 1) boundary[b++] = j * stride; // left, top to bottom

  return { positions, triangles, boundary };
}

/**
 * Fans a convex outline from its centroid and subdivides each sector into a
 * triangular lattice. Works for any convex polygon given counter-clockwise.
 */
export function tessellateConvexPolygon(
  outline: readonly Vec2[],
  segments: number,
): ShapeTessellation {
  if (outline.length < 3) {
    throw new Error('A footprint needs at least three outline points.');
  }

  const n = Math.max(1, Math.round(segments));
  const welder = new VertexWelder();
  const triangles: number[] = [];
  const boundary: number[] = [];

  let cx = 0;
  let cy = 0;
  for (const point of outline) {
    cx += point.x;
    cy += point.y;
  }
  cx /= outline.length;
  cy /= outline.length;

  for (let edge = 0; edge < outline.length; edge += 1) {
    const b = outline[edge];
    const c = outline[(edge + 1) % outline.length];

    const ux = b.x - cx;
    const uy = b.y - cy;
    const vx = c.x - cx;
    const vy = c.y - cy;

    // Barycentric lattice over the sector triangle (centroid, b, c).
    const grid: number[][] = [];
    for (let i = 0; i <= n; i += 1) {
      const column: number[] = [];
      for (let j = 0; j <= n - i; j += 1) {
        const x = cx + (ux * i) / n + (vx * j) / n;
        const y = cy + (uy * i) / n + (vy * j) / n;
        column.push(welder.add(x, y));
      }
      grid.push(column);
    }

    for (let i = 0; i < n; i += 1) {
      for (let j = 0; j < n - i; j += 1) {
        triangles.push(grid[i][j], grid[i + 1][j], grid[i][j + 1]);
        if (j < n - i - 1) {
          triangles.push(grid[i + 1][j], grid[i + 1][j + 1], grid[i][j + 1]);
        }
      }
    }

    // The outer edge runs from b to c; the last point is the next sector's first.
    for (let i = n; i > 0; i -= 1) {
      boundary.push(grid[i][n - i]);
    }
  }

  return {
    positions: welder.positions,
    triangles: Uint32Array.from(triangles),
    boundary: Uint32Array.from(boundary),
  };
}

/** Signed area of a polygon; positive means counter-clockwise. */
export function polygonArea(outline: readonly Vec2[]): number {
  let area = 0;
  for (let i = 0; i < outline.length; i += 1) {
    const a = outline[i];
    const b = outline[(i + 1) % outline.length];
    area += a.x * b.y - b.x * a.y;
  }
  return area / 2;
}

/**
 * Shrinks a convex outline inward by a constant distance.
 *
 * Each edge is moved along its inward normal and consecutive edges are
 * re-intersected, which is the correct offset for a convex polygon as long as
 * the distance stays below the inradius. Returns `null` when it does not,
 * because past that point the offset polygon has inverted rather than merely
 * become small — a caller needs to know the difference.
 */
export function offsetConvexPolygon(outline: readonly Vec2[], distance: number): Vec2[] | null {
  const count = outline.length;
  if (count < 3) return null;
  if (distance === 0) return outline.map((point) => ({ ...point }));

  // Each edge becomes a line, offset along its inward normal. Counter-clockwise
  // winding puts the interior to the left, so the inward normal of (dx, dy) is
  // (-dy, dx).
  const lines: Array<{ px: number; py: number; dx: number; dy: number }> = [];
  for (let i = 0; i < count; i += 1) {
    const a = outline[i];
    const b = outline[(i + 1) % count];
    const ex = b.x - a.x;
    const ey = b.y - a.y;
    const length = Math.hypot(ex, ey);
    if (length < 1e-9) return null;

    const dx = ex / length;
    const dy = ey / length;
    lines.push({ px: a.x - dy * distance, py: a.y + dx * distance, dx, dy });
  }

  const result: Vec2[] = [];
  for (let i = 0; i < count; i += 1) {
    const previous = lines[(i + count - 1) % count];
    const current = lines[i];

    const cross = previous.dx * current.dy - previous.dy * current.dx;
    if (Math.abs(cross) < 1e-9) return null;

    const t =
      ((current.px - previous.px) * current.dy - (current.py - previous.py) * current.dx) / cross;
    result.push({ x: previous.px + previous.dx * t, y: previous.py + previous.dy * t });
  }

  // A polygon offset past its inradius folds through itself. The area's sign
  // does not catch that: an over-offset triangle inverts through its incentre,
  // which is a 180-degree rotation, and rotation preserves winding. What does
  // catch it is the edges running backwards — each offset edge has to still
  // point the same way as the original it came from.
  for (let i = 0; i < count; i += 1) {
    const from = result[i];
    const to = result[(i + 1) % count];
    const line = lines[i];
    if ((to.x - from.x) * line.dx + (to.y - from.y) * line.dy <= 1e-9) return null;
  }

  return polygonArea(result) > 1e-6 ? result : null;
}
